import { useEffect, useMemo, useState } from 'react'

import type { CashCountPurpose, TellerSession } from '@/api/teller-types'
import { MoneyDisplay } from '@/components/money-display'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { formatMoney } from '@/lib/money'

import { CASH_COUNT_PURPOSES, GHS_COINS, GHS_NOTES, countPurposeLabel } from './constants'
import { useRecordCashCount } from './mutations'
import { TellerActionDialog } from './teller-action-dialog'

/**
 * The denomination count sheet.
 *
 * A count is the evidence behind a declaration, and the two have to agree: where a closing count
 * exists, the server refuses a declaration that does not add up to it
 * (`teller.declaration_disagrees_with_count`). So the total is computed here in integer pesewas —
 * pieces multiplied by a denomination, both integers — and never by scaling a decimal.
 *
 * Pieces are counted, not keyed as money, which is why they use a plain integer field rather than
 * `MoneyInput`: the thing being typed is "seven notes", and offering a decimal point there invites
 * an entry that means nothing.
 *
 * The sheet is laid out largest first, notes then coins, because that is the order a teller stacks
 * and reads a drawer. The one-pesewa coin is on it because a drawer that holds one has to be able
 * to say so.
 */

/** Blank is not zero: a denomination nobody looked at is different from one counted as empty. */
type Pieces = Record<number, number | null>

const emptySheet = (): Pieces => ({})

const INTEGER = /^\d{0,6}$/

export function CashCountDialog({
  session,
  open,
  onOpenChange,
  defaultPurpose = 'Closing',
}: {
  session: TellerSession
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultPurpose?: CashCountPurpose
}) {
  const [purpose, setPurpose] = useState<CashCountPurpose>(defaultPurpose)
  const [pieces, setPieces] = useState<Pieces>(emptySheet)
  const record = useRecordCashCount(session.id)

  useEffect(() => {
    if (!open) {
      setPurpose(defaultPurpose)
      setPieces(emptySheet())
      record.reset()
    }
    // `record` is a stable mutation object; clearing the sheet on close is the whole intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultPurpose])

  const lines = useMemo(
    () =>
      Object.entries(pieces)
        .map(([denomination, count]) => ({
          denominationMinor: Number(denomination),
          pieces: count ?? 0,
          entered: count !== null && count !== undefined,
        }))
        .filter((line) => line.entered),
    [pieces],
  )

  // Integer arithmetic throughout: a denomination is pesewas, a count is a whole number of pieces.
  const total = lines.reduce((sum, line) => sum + line.denominationMinor * line.pieces, 0)

  const expected = session.expectedClosingCashMinor
  const difference = total - expected

  const setCount = (denomination: number, raw: string) => {
    if (!INTEGER.test(raw)) {
      return
    }

    setPieces((current) => ({ ...current, [denomination]: raw === '' ? null : Number(raw) }))
  }

  const renderRow = (denomination: number) => (
    <tr key={denomination}>
      <td className="py-1 pr-2">
        <MoneyDisplay value={denomination} currency={session.currencyCode} className="text-left" />
      </td>
      <td className="py-1">
        <Input
          value={pieces[denomination] === null || pieces[denomination] === undefined ? '' : String(pieces[denomination])}
          onChange={(event) => setCount(denomination, event.target.value)}
          inputMode="numeric"
          autoComplete="off"
          className="h-8 text-right"
          // Through `formatMoney`, not a division by a hundred written out here: a screen reader
          // should hear "GH₵ 0.50", the same string the sighted row carries, not "0.5 cedis".
          aria-label={`Pieces counted of ${formatMoney(denomination, { currency: session.currencyCode })}`}
        />
      </td>
      <td className="py-1 pl-2">
        <MoneyDisplay
          value={pieces[denomination] ? denomination * (pieces[denomination] ?? 0) : null}
          currency={session.currencyCode}
          emphasis="muted"
        />
      </td>
    </tr>
  )

  return (
    <TellerActionDialog
      open={open}
      onOpenChange={onOpenChange}
      size="wide"
      title="Count the drawer"
      description={`Till ${session.tillNumber}. The sheet is the evidence a declaration is checked against.`}
      confirmLabel="Record the count"
      isPending={record.isPending}
      disabled={lines.length === 0}
      error={record.isError ? record.error : null}
      outcome={
        record.isSuccess ? (
          <Alert tone="success">
            <div className="space-y-1">
              <AlertTitle>Count recorded</AlertTitle>
              <AlertDescription>
                {countPurposeLabel(record.data.purpose)} of{' '}
                <MoneyDisplay
                  value={record.data.totalMinor}
                  currency={record.data.currencyCode}
                  className="inline-block text-left"
                />{' '}
                against an expected{' '}
                <MoneyDisplay
                  value={expected}
                  currency={session.currencyCode}
                  className="inline-block text-left"
                />
                . A count changes no balance — it is what the closing declaration must match.
              </AlertDescription>
            </div>
          </Alert>
        ) : undefined
      }
      onConfirm={() => {
        record.mutate({
          purpose,
          lines: lines.map((line) => ({
            denominationMinor: line.denominationMinor,
            pieces: line.pieces,
          })),
        })
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="count-purpose">Purpose</Label>
        <Select value={purpose} onValueChange={(value) => setPurpose(value as CashCountPurpose)}>
          <SelectTrigger id="count-purpose">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CASH_COUNT_PURPOSES.map((value) => (
              <SelectItem key={value} value={value}>
                {countPurposeLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted">
          A closing count is the teller&apos;s own count of their own drawer, and only they may record
          one. A spot count is a supervisor counting it mid-shift.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-1 text-2xs font-semibold tracking-wide text-muted uppercase">Notes</p>
          <table className="w-full text-sm">
            <tbody>{GHS_NOTES.map(renderRow)}</tbody>
          </table>
        </div>
        <div>
          <p className="mb-1 text-2xs font-semibold tracking-wide text-muted uppercase">Coins</p>
          <table className="w-full text-sm">
            <tbody>{GHS_COINS.map(renderRow)}</tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-3 rounded-card border border-border-subtle bg-surface-muted p-3 sm:grid-cols-3">
        <div>
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Counted</p>
          <MoneyDisplay
            value={lines.length === 0 ? null : total}
            currency={session.currencyCode}
            emphasis="strong"
            className="text-left"
          />
        </div>
        <div>
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Expected</p>
          <MoneyDisplay value={expected} currency={session.currencyCode} className="text-left" />
        </div>
        <div>
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Difference</p>
          <MoneyDisplay
            value={lines.length === 0 ? null : difference}
            currency={session.currencyCode}
            signed
            colourNegative
            className="text-left"
          />
        </div>
      </div>

      <p className="text-xs text-muted">
        Leave a denomination blank if there is none of it. An empty drawer is declared by keying 0
        against one line — a sheet with nothing on it at all is refused as{' '}
        <span className="font-mono">teller.count_empty</span>.
      </p>
    </TellerActionDialog>
  )
}
